/**
 * Apollo Client Configuration for Nackahemtjanst
 * Follows package configuration pattern - app reads env and passes config
 */

import { createApolloClient, getApolloClient } from "@appcaire/graphql";
import { ApolloProvider, type NormalizedCacheObject } from "@apollo/client";
import { getGraphqlUrl, getGraphqlWsUrl } from "./env";

/**
 * Returns the default GraphQL HTTP URL.
 *
 * For SSR (server-side), we use an absolute URL.
 * For client-side, we use a relative URL so Vite can proxy it in development.
 */
function getDefaultGraphqlUrl(): string {
  // During SSR, use absolute URL
  if (typeof window === "undefined") {
    return "http://localhost:4005/graphql";
  }
  // Client-side: use relative URL for Vite proxy
  return "/api/graphql";
}

/**
 * Returns the default GraphQL WS URL.
 *
 * For SSR (server-side), we use an absolute URL.
 * For client-side, we derive it from the current origin.
 */
function getDefaultGraphqlWsUrl(): string {
  // During SSR, use absolute URL
  // Check for window.location.origin explicitly to avoid errors
  try {
    if (
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof window.location.origin === "undefined"
    ) {
      return "ws://localhost:4005/graphql";
    }
    // Client-side: derive from current origin
    return window.location.origin.replace(/^http/, "ws") + "/api/graphql";
  } catch {
    // Fallback for SSR
    return "ws://localhost:4005/graphql";
  }
}

// Lazy Apollo Client creation to avoid window access during SSR module load
let apolloClientInstance: ReturnType<typeof getApolloClient> | null = null;

/**
 * Create an anonymous Apollo client for one SSR request.
 *
 * The browser client is intentionally a singleton, while the server client
 * must be request-scoped so normalized provider data can never cross response
 * boundaries.
 */
export function createSsrApolloClient(
  graphqlUrl: string,
  abortSignal?: AbortSignal,
) {
  if (!/^https?:\/\//i.test(graphqlUrl)) {
    throw new Error("SSR GraphQL URL must be absolute");
  }

  let operationError: Error | null = null;
  const client = createApolloClient({
    graphqlUrl,
    connectToDevTools: false,
    ssrMode: true,
    abortSignal,
    onOperationError: (error) => {
      operationError ??= error;
    },
    getToken: async () => null,
  });

  return {
    client,
    getOperationError: () => operationError,
  };
}

function getApolloClientInstance() {
  if (!apolloClientInstance) {
    // App reads environment variables and passes config to package
    // Use env utility which handles both server-side (process.env) and client-side (window.__VITE_ENV__)
    const graphqlUrl = getGraphqlUrl() || getDefaultGraphqlUrl();
    const graphqlWsUrl = getGraphqlWsUrl() || getDefaultGraphqlWsUrl();

    // Create Apollo Client with configuration
    const isDevelopment =
      typeof import.meta !== "undefined" && import.meta.env
        ? import.meta.env.MODE === "development"
        : typeof process !== "undefined" &&
          process.env.NODE_ENV !== "production";

    apolloClientInstance = getApolloClient({
      graphqlUrl,
      graphqlWsUrl,
      connectToDevTools: isDevelopment,
      ssrForceFetchDelay: 100,
      getToken: async () => {
        // SEO apps don't require authentication
        return null;
      },
    });
  }
  return apolloClientInstance;
}

/** Restore the server-fetched public route data before React hydrates. */
export function restoreApolloState(
  initialState: NormalizedCacheObject | undefined,
): void {
  if (!initialState) return;
  getApolloClientInstance().cache.restore(initialState);
}

// Export the getter function
export const apolloClient = new Proxy(
  {} as ReturnType<typeof getApolloClient>,
  {
    get(_target, prop) {
      return getApolloClientInstance()[
        prop as keyof ReturnType<typeof getApolloClient>
      ];
    },
  },
);

// Export provider component for convenience
export { ApolloProvider };
